import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger } from './select';
import { X } from 'lucide-react';
import Typography from './typography';

interface MultiSelectChipsProps {
  placeholder: string;
  options: { value: string; label: string }[];
  selectedValues: string[];
  onSelectionChange: (values: string[]) => void;
  className?: string;
  disabled?: boolean;
  mode?: 'multi' | 'single'; // New prop for mode selection
}

const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
  placeholder,
  options,
  selectedValues,
  onSelectionChange,
  className = '',
  disabled = false,
  mode = 'multi', // Default to multi-select mode
}) => {
  const [selectedOption, setSelectedOption] = useState('');

  const handleSelectChange = (value: string) => {
    if (disabled) return;

    if (mode === 'single') {
      // Single select mode: replace the current selection
      onSelectionChange([value]);
      setSelectedOption('');
    } else {
      // Multi select mode: add to existing selection
      if (value && !selectedValues.includes(value)) {
        onSelectionChange([...selectedValues, value]);
        setSelectedOption('');
      }
    }
  };

  const handleRemove = (valueToRemove: string) => {
    if (disabled) return;
    const newValues = selectedValues.filter((value) => value !== valueToRemove);
    onSelectionChange(newValues);
  };

  // Prevent event propagation handler
  const stopPropagation = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className={className}>
      <Select
        value={selectedOption}
        onValueChange={handleSelectChange}
        disabled={disabled}
      >
        <SelectTrigger
          className={`w-full min-h-[40px] px-1 py-1 border border-gray-300 rounded-md bg-white relative font-inter ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          style={{ height: 'auto' }}
        >
          <div className="w-full">
            {selectedValues.length > 0 ? (
              <div className="flex flex-wrap gap-1 py-0.5">
                {selectedValues.map((value) => {
                  const option = options.find((opt) => opt.value === value);
                  return (
                    <div
                      key={value}
                      className="inline-flex items-center gap-1 p-1 bg-primary-base/10 rounded-md border border-primary-base/30 flex-shrink-0"
                    >
                      <Typography
                        variant="small"
                        weight="medium"
                        size="12"
                        className="text-primary-base font-inter whitespace-nowrap leading-none"
                      >
                        {option?.label || value}
                      </Typography>
                      {!disabled && mode === 'multi' && (
                        <button
                          onPointerDown={stopPropagation}
                          onMouseDown={stopPropagation}
                          onClick={(e) => {
                            stopPropagation(e);
                            handleRemove(value);
                          }}
                          className="text-primary-base cursor-pointer rounded-full hover:bg-primary-base/20 flex-shrink-0 ml-1"
                          type="button"
                        >
                          <X className="h-3 w-3 text-primary-base" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="py-0.5">
                <Typography
                  variant="small"
                  color="neutral400"
                  weight="normal"
                  size="12"
                  className="text-gray-500"
                >
                  Select {placeholder.toLowerCase()}
                </Typography>
              </div>
            )}
          </div>
        </SelectTrigger>
        <SelectContent>
          {options
            .filter((option) => !selectedValues.includes(option.value))
            .map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default MultiSelectChips;
